import { useCallback, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ActivityView, type CommentView } from "../api/client";
import { clipboardFiles } from "../hooks/useFileDrop";
import { Avatar } from "./Avatar";
import { ConfirmButton } from "./ConfirmButton";
import { Markdown } from "./Markdown";
import { MentionTextarea } from "./MentionTextarea";
import { formatRelativeTime } from "../util/date";
import { describe } from "../util/activity";

interface CardActivityProps {
  cardId: string;
  /** Needed for @mention autocomplete: the mentionable set is the board's members. */
  boardId: string;
  comments: CommentView[];
  activity: ActivityView[];
  canEdit: boolean;
  currentUserId: string | undefined;
  /** Called when files are pasted into the comment box (attach, don't quote). */
  onPasteFiles?: (files: FileList) => void;
}

type Item =
  | { kind: "comment"; at: string; comment: CommentView }
  | { kind: "activity"; at: string; activity: ActivityView };

const draftKey = (cardId: string) => `kandoo:comment-draft:${cardId}`;

/**
 * The comment box's text, kept in localStorage per card.
 *
 * An unsent comment is real work, and the modal holding it can go away for
 * plenty of reasons — a stray backdrop click, a navigation, a reload, a
 * transient error unmounting the tree. Component state alone loses it every
 * time; this brings it back when the card is reopened.
 */
function usePersistentDraft(cardId: string) {
  const [draft, setDraft] = useState(() => {
    try {
      return localStorage.getItem(draftKey(cardId)) ?? "";
    } catch {
      return "";
    }
  });

  // re-seed when the modal is reused for a different card
  useEffect(() => {
    try {
      setDraft(localStorage.getItem(draftKey(cardId)) ?? "");
    } catch {
      setDraft("");
    }
  }, [cardId]);

  const update = useCallback(
    (next: string) => {
      setDraft(next);
      try {
        if (next) localStorage.setItem(draftKey(cardId), next);
        else localStorage.removeItem(draftKey(cardId));
      } catch {
        // private mode / quota — the draft just won't outlive the component
      }
    },
    [cardId],
  );

  return [draft, update] as const;
}

export function CardActivity({
  cardId,
  boardId,
  comments,
  activity,
  canEdit,
  currentUserId,
  onPasteFiles,
}: CardActivityProps) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["card", cardId] });

  const addComment = useMutation({
    mutationFn: (body: string) => api.comments.create(cardId, { body }),
    onSuccess: invalidate,
  });
  const editComment = useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      api.comments.update(id, body),
    onSuccess: invalidate,
  });
  const deleteComment = useMutation({
    mutationFn: (id: string) => api.comments.delete(id),
    onSuccess: invalidate,
  });

  const [draft, setDraft] = usePersistentDraft(cardId);

  // merge comments + activity into one timeline, newest first
  const items: Item[] = [
    ...comments.map((c) => ({ kind: "comment" as const, at: c.createdAt, comment: c })),
    ...activity.map((a) => ({ kind: "activity" as const, at: a.createdAt, activity: a })),
  ].sort((x, y) => (x.at < y.at ? 1 : -1));

  return (
    <section className="mt-6">
      <h2 className="mb-2 font-medium">Activity</h2>

      {canEdit && (
        <div className="mb-4 flex gap-2">
          <Avatar userId={currentUserId ?? "me"} name="You" />
          <div className="flex-1">
            <MentionTextarea
              boardId={boardId}
              value={draft}
              onChange={setDraft}
              onPaste={(e) => {
                const files = clipboardFiles(e);
                if (!files || !onPasteFiles) return;
                e.preventDefault();
                onPasteFiles(files);
              }}
              placeholder="Write a comment… @ to mention someone"
              rows={2}
              className="w-full resize-none rounded border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-blue-400"
            />
            {draft.trim() && (
              <div className="mt-1">
                <button
                  type="button"
                  onClick={() =>
                    addComment.mutate(draft.trim(), {
                      onSuccess: () => setDraft(""),
                    })
                  }
                  className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
                >
                  Comment
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-slate-500">No activity yet.</p>
      ) : (
        <ul className="space-y-3">
          {items.map((it) =>
            it.kind === "comment" ? (
              <CommentRow
                key={`c-${it.comment.id}`}
                comment={it.comment}
                canManage={canEdit && it.comment.userId === currentUserId}
                onEdit={(body) => editComment.mutate({ id: it.comment.id, body })}
                onDelete={() => deleteComment.mutate(it.comment.id)}
              />
            ) : (
              <li key={`a-${it.activity.id}`} className="flex items-start gap-2 text-sm">
                <Avatar
                  userId={it.activity.userId ?? "system"}
                  name={
                    it.activity.actorDisplayName ??
                    it.activity.actorUsername ??
                    "?"
                  }
                />
                <div className="pt-0.5 text-slate-600">
                  <span className="font-medium text-slate-800">
                    {it.activity.actorDisplayName ??
                      it.activity.actorUsername ??
                      "Someone"}
                  </span>{" "}
                  {describe(it.activity)}
                  <span className="ml-1.5 text-xs text-slate-400">
                    {formatRelativeTime(it.activity.createdAt)}
                  </span>
                </div>
              </li>
            ),
          )}
        </ul>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function CommentRow({
  comment,
  canManage,
  onEdit,
  onDelete,
}: {
  comment: CommentView;
  canManage: boolean;
  onEdit: (body: string) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const name = comment.authorDisplayName || comment.authorUsername;

  return (
    <li className="flex items-start gap-2">
      <Avatar userId={comment.userId} name={name} />
      <div className="flex-1">
        <div className="rounded-lg bg-slate-100 px-3 py-2 text-sm">
          <div className="mb-0.5 flex items-center gap-2">
            <span className="font-medium text-slate-800">{name}</span>
            <span className="text-xs text-slate-400">
              {formatRelativeTime(comment.createdAt)}
              {comment.updatedAt !== comment.createdAt && " (edited)"}
            </span>
          </div>
          {editing ? (
            <div>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={2}
                className="w-full resize-none rounded border border-slate-300 px-2 py-1 text-sm outline-none focus:border-blue-400"
              />
              <div className="mt-1 flex gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => {
                    const b = draft.trim();
                    if (b && b !== comment.body) onEdit(b);
                    setEditing(false);
                  }}
                  className="rounded bg-blue-600 px-2 py-1 text-white hover:bg-blue-700"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDraft(comment.body);
                    setEditing(false);
                  }}
                  className="text-slate-500 hover:underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <Markdown>{comment.body}</Markdown>
          )}
        </div>
        {canManage && !editing && (
          <div className="mt-0.5 flex gap-3 pl-1 text-xs text-slate-400">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="hover:text-slate-700 hover:underline"
            >
              Edit
            </button>
            <ConfirmButton
              onConfirm={onDelete}
              className="hover:text-red-600 hover:underline"
              confirmClassName="text-red-600 underline"
              confirmLabel="Delete?"
            >
              Delete
            </ConfirmButton>
          </div>
        )}
      </div>
    </li>
  );
}
